"use client";

import { createContext, useContext } from "react";
import { useSearchInfiniteContext } from "../../hooks/useSearchInfinite";

/**
 * @typedef {import("react").FC<{ error: import("../../types/alfred/ApiError").ApiError | null; mutate: () => void; status: number | null; }>} DataHandlerInfiniteErrorComponent
 */

/**
 * @template T
 * @param {object} props
 * @param {import("swr/infinite").SWRInfiniteResponse<import("../../hooks/useApiFetcher").ApiFetcherReturn<T>>} props.swrInfiniteResponse the SWR response
 * @param {import("react").ReactNode | null} [props.loadingElement] the element to render if the data is loading
 * @param {import("react").ReactElement | null} [props.noResultElement] the element to render if the data is empty
 * @param {boolean} [props.renderErrorComponent] if true, the error element will be rendered
 * @param {boolean} [props.renderWithEmptyData] if true, the children will be rendered even if the data is empty
 * @param {boolean} [props.keepPreviousData]
 * @param {(response: import("../../hooks/useApiFetcher").ApiFetcherReturn<T>[]) => import("react").ReactElement} [props.children]
 */
export function DataHandlerInfinite({
  swrInfiniteResponse,
  loadingElement,
  noResultElement,
  renderErrorComponent = true,
  renderWithEmptyData = false,
  keepPreviousData = false,
  children,
}) {
  const {
    loadingElement: defaultLoadingElement,
    ErrorComponent,
    noResultElement: DefaultNoResultElement,
  } = useDataHandlerInfinite();

  const { form, autoSubmit } = useSearchInfiniteContext();

  if (
    (form.formState.isDirty &&
      autoSubmit === true &&
      keepPreviousData === false) ||
    (swrInfiniteResponse.isValidating &&
      (keepPreviousData === false || swrInfiniteResponse.data === undefined))
  ) {
    return loadingElement === undefined
      ? defaultLoadingElement
      : loadingElement;
  }

  if (swrInfiniteResponse.data !== undefined) {
    const apiResponses = swrInfiniteResponse.data;
    if (
      apiResponses.length === 1 &&
      // @ts-ignore
      (apiResponses[0]?.parsedJson?.data?.length ?? 0) === 0 &&
      !renderWithEmptyData
    ) {
      return noResultElement === undefined
        ? DefaultNoResultElement
        : noResultElement;
    }
    return children?.(swrInfiniteResponse.data);
  }

  if (!swrInfiniteResponse.isValidating && swrInfiniteResponse.error) {
    return renderErrorComponent ? (
      <ErrorComponent
        error={swrInfiniteResponse.error?.parsedJson}
        mutate={swrInfiniteResponse.mutate}
        status={swrInfiniteResponse.error?.response?.status ?? null}
      />
    ) : null;
  }

  return null;
}

/**
 * @typedef {object} DataHandlerInfiniteContextValue
 * @prop {import("react").ReactElement | null} noResultElement
 * @prop {DataHandlerInfiniteErrorComponent} ErrorComponent
 * @prop {import("react").ReactElement | null} loadingElement
 */

/** @type {DataHandlerInfiniteContextValue} */
const DEFAULT_VALUE = {
  loadingElement: null,
  ErrorComponent: () => null,
  noResultElement: null,
};

const DataHandlerInfiniteContext = createContext(DEFAULT_VALUE);

/**
 * @returns {DataHandlerInfiniteContextValue}
 */
export function useDataHandlerInfinite() {
  const value = useContext(DataHandlerInfiniteContext);
  if (value === DEFAULT_VALUE) {
    throw new Error(
      "useDataHandlerInfinite must be used within a DataHandlerProvider",
    );
  }
  return value;
}

/**
 * @typedef {object} Props
 * @prop {DataHandlerInfiniteContextValue} value
 * @prop {import("react").ReactNode} children
 */
/**
 * @param {Props} props
 */
export function DataHandlerInfiniteProvider({ value, children }) {
  return (
    <DataHandlerInfiniteContext.Provider value={value} children={children} />
  );
}
